import type {
  BillingStatus,
  CompanyStatus,
  DeploymentMode,
  PlanCode,
} from '../tenancy/company.dto.js';
import type { LocationKind } from '../tenancy/location.dto.js';
import type { StationDeviceClaimStatus } from '../tenancy/station-recovery.dto.js';
import type { StationKind, StationStatus } from '../tenancy/station.dto.js';
import type { StoreOperatingModel } from '../tenancy/store.dto.js';
import type { UserStatus } from '../users/index.js';
import type {
  MaterialCaptureType,
  MaterialCaptureUnit,
  MaterialPresentationType,
} from '../materials-catalog/materials-catalog.dto.js';

export interface PlatformSystemCompanySummary {
  id: string;
  name: string;
  receiverDirectoryScopeId?: string | null;
  status: CompanyStatus;
  deploymentMode: DeploymentMode;
  planCode: PlanCode;
  billingStatus: BillingStatus;
  trialExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListPlatformSystemCompaniesRequest {
  status?: CompanyStatus;
  deploymentMode?: DeploymentMode;
  planCode?: PlanCode;
  billingStatus?: BillingStatus;
  page?: number;
  pageSize?: number;
}

export interface ListPlatformSystemCompaniesResponse {
  items: PlatformSystemCompanySummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListPlatformAuditLogsRequest {
  action?: string;
  entityType?: string;
  entityId?: string;
  permissionKey?: string;
  actorUserId?: string;
  occurredFrom?: string;
  occurredTo?: string;
  page?: number;
  pageSize?: number;
}

export type PlatformAuditLogMetadataSummaryEntryKind =
  | 'value'
  | 'fieldChange'
  | 'count'
  | 'listChange'
  | 'notice';

export interface PlatformAuditLogMetadataSummaryEntry {
  kind: PlatformAuditLogMetadataSummaryEntryKind;
  label: string;
  value?: string | number;
  before?: string | null;
  after?: string | null;
  beforeValues?: string[];
  afterValues?: string[];
  truncatedBeforeCount?: number;
  truncatedAfterCount?: number;
}

export interface PlatformAuditLogDiagnostics {
  auditLogId: string;
  actorUserId: string;
  entityId: string;
  metadataKeys: string[];
  createdAt: string;
}

export interface PlatformAuditLogItem {
  occurredAt: string;
  action: string;
  entityType: string;
  entityLabel: string;
  actorEmail: string;
  permissionKey: string;
  permissionLabel: string;
  metadataSummary: PlatformAuditLogMetadataSummaryEntry[];
  diagnostics: PlatformAuditLogDiagnostics;
}

export interface ListPlatformAuditLogsResponse {
  items: PlatformAuditLogItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListPlatformPermissionGrantsRequest {
  userEmail?: string;
  permissionKey?: string;
  page?: number;
  pageSize?: number;
}

export interface PlatformPermissionGrantSummary {
  permissionKey: string;
  permissionDescription: string;
  grantedAt: string;
}

export interface PlatformPermissionGrantDiagnostics {
  userId: string;
  storage: 'platform_permission_grants';
}

export interface PlatformPermissionGrantUserItem {
  userEmail: string;
  userStatus: UserStatus;
  grantCount: number;
  grantedAtFirst: string;
  grantedAtLatest: string;
  grants: PlatformPermissionGrantSummary[];
  diagnostics: PlatformPermissionGrantDiagnostics;
}

export interface ListPlatformPermissionGrantsResponse {
  items: PlatformPermissionGrantUserItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AssignablePlatformPermission {
  key: string;
  description: string;
}

export interface ListAssignablePlatformGrantPermissionsResponse {
  items: AssignablePlatformPermission[];
}

export interface AddPlatformPermissionGrantRequest {
  userEmail: string;
  permissionKey: string;
}

export interface RevokePlatformPermissionGrantRequest {
  userId: string;
  permissionKey: string;
}

export type PlatformPermissionGrantMutationKind = 'added' | 'revoked' | 'unchanged';

export interface PlatformPermissionGrantMutationSummary {
  kind: PlatformPermissionGrantMutationKind;
  targetUserEmail: string;
  targetUserStatus: UserStatus;
  permissionKey: string;
  revokedSessionsCount: number;
  grantCountBefore: number;
  grantCountAfter: number;
  selfChange: boolean;
  remainingGrantManagerCount: number;
  remainingPlatformGrantedUserCount: number;
}

export interface MutatePlatformPermissionGrantResponse {
  mutation: PlatformPermissionGrantMutationSummary;
  targetUser: PlatformPermissionGrantUserItem | null;
}

export const platformSystemCompanyAuditActionValues = [
  'platformSystem.companyBilling.updated',
  'platformSystem.companyPlan.updated',
  'platformSystem.companyStatus.updated',
] as const;

export type PlatformSystemCompanyAuditAction =
  (typeof platformSystemCompanyAuditActionValues)[number];

export interface ListPlatformSystemCompanyAuditLogsRequest {
  companyId: string;
  action?: PlatformSystemCompanyAuditAction;
  actorUserId?: string;
  occurredFrom?: string;
  occurredTo?: string;
  page?: number;
  pageSize?: number;
}

export type PlatformSystemCompanyAuditLogMetadataSummaryEntry =
  PlatformAuditLogMetadataSummaryEntry;

export interface PlatformSystemCompanyAuditLogDiagnostics {
  auditLogId: string;
  actorUserId: string | null;
  companyId: string;
  entityId: string;
  metadataKeys: string[];
  createdAt: string;
  storage: 'audit_logs';
}

export interface PlatformSystemCompanyAuditLogCompanyContext {
  companyId: string;
  companyName: string;
  companyStatus: CompanyStatus;
}

export interface PlatformSystemCompanyAuditLogItem {
  source: 'companySystem';
  sourceLabel: 'Company system event';
  action: PlatformSystemCompanyAuditAction;
  eventLabel: string;
  occurredAt: string;
  actorEmail: string;
  permissionKey: string | null;
  permissionLabel: string;
  metadataSummary: PlatformSystemCompanyAuditLogMetadataSummaryEntry[];
  diagnostics: PlatformSystemCompanyAuditLogDiagnostics;
}

export interface ListPlatformSystemCompanyAuditLogsResponse {
  company: PlatformSystemCompanyAuditLogCompanyContext;
  items: PlatformSystemCompanyAuditLogItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListPlatformSystemPlanAffectedCompaniesRequest {
  planCode: PlanCode;
  page?: number;
  pageSize?: number;
}

export interface ListPlatformSystemPlanAffectedCompaniesResponse {
  planCode: PlanCode;
  items: PlatformSystemCompanySummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListPlatformSystemFeatureAffectedCompaniesRequest {
  featureKey: string;
  page?: number;
  pageSize?: number;
}

export interface ListPlatformSystemFeatureAffectedCompaniesResponse {
  feature: PlatformSystemFeatureSummary;
  mappedPlanCodes: PlanCode[];
  items: PlatformSystemCompanySummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface GetPlatformSystemCompanyDetailRequest {
  companyId: string;
}

export interface PlatformSystemCompanyTopologySummary {
  storesCount: number;
  monoStationStoresCount: number;
  multiStationStoresCount: number;
  locationsCount: number;
  activeLocationsCount: number;
  inactiveLocationsCount: number;
  stationsCount: number;
  activeStationsCount: number;
  disabledStationsCount: number;
}

export interface PlatformSystemCompanyMembershipSummary {
  membershipsCount: number;
  activeMembershipsCount: number;
  invitedMembershipsCount: number;
  disabledMembershipsCount: number;
}

export interface PlatformSystemCompanyDetail {
  company: PlatformSystemCompanySummary;
  topology: PlatformSystemCompanyTopologySummary;
  memberships: PlatformSystemCompanyMembershipSummary;
  enabledFeatureKeys: string[];
}

export interface GetPlatformSystemCompanyDetailResponse {
  companyDetail: PlatformSystemCompanyDetail;
}

export const platformSystemDeploymentModeImpactTransitionValues = [
  'same_mode',
  'offline_only_to_hybrid',
  'offline_only_to_saas',
  'hybrid_to_offline_only',
  'hybrid_to_saas',
  'saas_to_offline_only',
  'saas_to_hybrid',
] as const;
export type PlatformSystemDeploymentModeImpactTransition =
  (typeof platformSystemDeploymentModeImpactTransitionValues)[number];

export const platformSystemDeploymentModeImpactTransitionStatusValues = [
  'same_mode',
  'deferred',
  'requires_design',
  'blocked',
] as const;
export type PlatformSystemDeploymentModeImpactTransitionStatus =
  (typeof platformSystemDeploymentModeImpactTransitionStatusValues)[number];

export const platformSystemDeploymentModeImpactRiskLevelValues = [
  'none',
  'high',
  'critical',
] as const;
export type PlatformSystemDeploymentModeImpactRiskLevel =
  (typeof platformSystemDeploymentModeImpactRiskLevelValues)[number];

export interface GetPlatformSystemCompanyDeploymentModeImpactRequest {
  companyId: string;
  requestedMode: DeploymentMode;
}

export interface PlatformSystemDeploymentModeImpactCounts {
  storesCount: number;
  monoStationStoresCount: number;
  multiStationStoresCount: number;
  locationsCount: number;
  activeLocationsCount: number;
  inactiveLocationsCount: number;
  stationsCount: number;
  activeStationsCount: number;
  disabledStationsCount: number;
  activeDeviceClaimCount: number;
  claimedActiveStationCount: number;
  openStoreOperatingSessionCount: number;
  cloudReadinessReportCount: number;
  readinessParticipantCount: number;
  pendingReadinessBlockingEventCount: number;
}

export interface PlatformSystemDeploymentModeImpactCloudFacts {
  source: 'cloud_api';
  companyStatus: CompanyStatus;
  planCode: PlanCode;
  billingStatus: BillingStatus;
  trialExpiresAt: string | null;
  receiverDirectoryScopeId: string | null;
  createdAt: string;
  updatedAt: string;
  enabledFeatureKeys: string[];
}

export interface PlatformSystemDeploymentModeImpactUnknownLocalFact {
  key: string;
  label: string;
  status: 'not_inspected';
  description: string;
}

export interface PlatformSystemDeploymentModeImpactDiagnostics {
  companyId: string;
  generatedAt: string;
  source: 'cloud_api';
  sourceTables: string[];
  notes: string[];
}

export interface PlatformSystemDeploymentModeImpactPreview {
  company: PlatformSystemCompanySummary;
  currentMode: DeploymentMode;
  requestedMode: DeploymentMode;
  transition: PlatformSystemDeploymentModeImpactTransition;
  mutationSupported: false;
  transitionStatus: PlatformSystemDeploymentModeImpactTransitionStatus;
  riskLevel: PlatformSystemDeploymentModeImpactRiskLevel;
  counts: PlatformSystemDeploymentModeImpactCounts;
  cloudFacts: PlatformSystemDeploymentModeImpactCloudFacts;
  unknownLocalFacts: PlatformSystemDeploymentModeImpactUnknownLocalFact[];
  requiredFutureSteps: string[];
  warnings: string[];
  diagnostics: PlatformSystemDeploymentModeImpactDiagnostics;
}

export interface GetPlatformSystemCompanyDeploymentModeImpactResponse {
  preview: PlatformSystemDeploymentModeImpactPreview;
}

export const platformSystemReceiverDirectoryScopeTransitionStatusValues = [
  'same_scope',
  'deferred',
] as const;
export type PlatformSystemReceiverDirectoryScopeTransitionStatus =
  (typeof platformSystemReceiverDirectoryScopeTransitionStatusValues)[number];

export const platformSystemReceiverDirectoryScopeRiskLevelValues = ['none', 'high'] as const;
export type PlatformSystemReceiverDirectoryScopeRiskLevel =
  (typeof platformSystemReceiverDirectoryScopeRiskLevelValues)[number];

export interface PreviewPlatformSystemCompanyReceiverDirectoryScopeRequest {
  targetScopeId: string;
}

export interface PlatformSystemReceiverDirectoryCurrentScope {
  normalizedScopeId: string;
  storedScopeId: string | null;
  source: 'stored_company_scope' | 'company_id_legacy_fallback';
}

export interface PlatformSystemReceiverDirectoryTargetScope {
  scopeId: string;
  inputKind: 'uuid';
  ownership: 'not_evaluated';
}

export interface PlatformSystemReceiverDirectoryScopeImpactCounts {
  companyOwnedReceiversInCurrentScopeCount: number;
  companyOwnedReceiversOutsideCurrentScopeCount: number;
  companyOwnedReceiversInTargetScopeCount: number;
  shipmentReceiverReferenceCount: number;
  inboundReceiptReceiverReferenceCount: number;
}

export interface PlatformSystemReceiverDirectoryScopeCloudFacts {
  source: 'cloud_api';
  companyStatus: CompanyStatus;
  planCode: PlanCode;
  billingStatus: BillingStatus;
  trialExpiresAt: string | null;
  currentScopeStorage: 'company.receiverDirectoryScopeId' | 'company.id_legacy_fallback';
  countBoundary: 'owner_company_only';
  targetScopeOwnership: 'not_evaluated';
  crossCompanyScopeDisclosure: 'not_disclosed';
  createdAt: string;
  updatedAt: string;
  enabledFeatureKeys: string[];
}

export interface PlatformSystemReceiverDirectoryScopeUnknownLocalFact {
  key: string;
  label: string;
  status: 'not_inspected';
  description: string;
}

export interface PlatformSystemReceiverDirectoryScopeDiagnostics {
  companyId: string;
  normalizedCurrentScopeId: string;
  targetScopeId: string;
  storedReceiverDirectoryScopeId: string | null;
  generatedAt: string;
  source: 'cloud_api';
  sourceTables: string[];
  notes: string[];
}

export interface PlatformSystemReceiverDirectoryScopePreview {
  company: PlatformSystemCompanySummary;
  currentScope: PlatformSystemReceiverDirectoryCurrentScope;
  targetScope: PlatformSystemReceiverDirectoryTargetScope;
  normalizedCurrentScopeId: string;
  targetScopeId: string;
  sameScope: boolean;
  mutationSupported: false;
  transitionStatus: PlatformSystemReceiverDirectoryScopeTransitionStatus;
  riskLevel: PlatformSystemReceiverDirectoryScopeRiskLevel;
  counts: PlatformSystemReceiverDirectoryScopeImpactCounts;
  cloudFacts: PlatformSystemReceiverDirectoryScopeCloudFacts;
  desktopLocalState: 'not_inspected';
  unknownLocalFacts: PlatformSystemReceiverDirectoryScopeUnknownLocalFact[];
  warnings: string[];
  requiredFutureWork: string[];
  diagnostics: PlatformSystemReceiverDirectoryScopeDiagnostics;
}

export interface PreviewPlatformSystemCompanyReceiverDirectoryScopeResponse {
  preview: PlatformSystemReceiverDirectoryScopePreview;
}

export const platformSystemStationClaimStatusFilterValues = [
  'active',
  'superseded',
  'failed',
  'expired',
  'none',
] as const;
export type PlatformSystemStationClaimStatusFilter =
  (typeof platformSystemStationClaimStatusFilterValues)[number];

export const platformSystemStationDataAvailabilityStatusValues = ['not_reported_to_cloud'] as const;
export type PlatformSystemStationDataAvailabilityStatus =
  (typeof platformSystemStationDataAvailabilityStatusValues)[number];

export interface PlatformSystemCompanyStationContext {
  companyId: string;
  companyName: string;
  companyStatus: CompanyStatus;
}

export interface PlatformSystemCompanyStationDiagnostics {
  companyId: string;
  stationId: string;
  storeId: string;
  locationId: string;
}

export interface PlatformSystemStationAssignmentSummary {
  stationName: string;
  stationKind: StationKind;
  stationStatus: StationStatus;
  isOfflineCapable: boolean;
  storeName: string;
  storeCode: string | null;
  storeOperatingModel: StoreOperatingModel;
  locationName: string;
  locationCode: string;
  locationKind: LocationKind;
  locationIsActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformSystemStationDeviceClaimDiagnostics {
  claimId: string;
  deviceId: string;
  previousDeviceClaimId: string | null;
  supersededByClaimId: string | null;
}

export interface PlatformSystemStationDeviceClaimSummary {
  claimStatus: StationDeviceClaimStatus;
  authorizedAt: string;
  activatedAt: string | null;
  supersededAt: string | null;
  failedAt: string | null;
  expiresAt: string;
  desktopReleaseChannel: string | null;
  desktopAppVersion: string | null;
  diagnostics: PlatformSystemStationDeviceClaimDiagnostics;
}

export interface PlatformSystemStationReadinessDiagnostics {
  sessionKey: string;
  primaryStationId: string;
}

export interface PlatformSystemStationReadinessParticipantDiagnostics {
  stationId: string;
}

export interface PlatformSystemStationReadinessParticipant {
  stationName: string | null;
  joinedAt: string;
  persistedLocally: boolean;
  lastReadinessConfirmedAt: string | null;
  pendingBlockingEventCount: number;
  blockingReason: string;
  lastHeartbeatAt: string | null;
  diagnostics: PlatformSystemStationReadinessParticipantDiagnostics;
}

export interface PlatformSystemStationReadinessReportSummary {
  businessDate: string;
  sessionStatus: 'OPEN' | 'CLOSED';
  timeZone: string;
  cloudVisibleAt: string | null;
  openedAt: string;
  closedAt: string | null;
  participantCount: number;
  confirmedParticipantCount: number;
  pendingBlockingEventCount: number;
  latestReadinessConfirmedAt: string | null;
  latestHeartbeatAt: string | null;
  updatedAt: string;
  diagnostics: PlatformSystemStationReadinessDiagnostics;
}

export interface PlatformSystemStationReadinessReportDetail
  extends PlatformSystemStationReadinessReportSummary {
  participants: PlatformSystemStationReadinessParticipant[];
}

export interface PlatformSystemStationDataAvailabilityRow {
  key: string;
  label: string;
  status: PlatformSystemStationDataAvailabilityStatus;
  description: string;
}

export interface PlatformSystemStationSupportDiagnosticRow {
  label: string;
  value: string | null;
}

export interface PlatformSystemCompanyStationListItem {
  assignment: PlatformSystemStationAssignmentSummary;
  activeDeviceClaim: PlatformSystemStationDeviceClaimSummary | null;
  latestReadinessReport: PlatformSystemStationReadinessReportSummary | null;
  diagnostics: PlatformSystemCompanyStationDiagnostics;
}

export interface ListPlatformSystemCompanyStationsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  stationStatus?: StationStatus;
  claimStatus?: PlatformSystemStationClaimStatusFilter;
  q?: string;
  businessDate?: string;
  page?: number;
  pageSize?: number;
}

export interface ListPlatformSystemCompanyStationsResponse {
  company: PlatformSystemCompanyStationContext;
  items: PlatformSystemCompanyStationListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface GetPlatformSystemCompanyStationDetailRequest {
  companyId: string;
  stationId: string;
  businessDate?: string;
}

export interface PlatformSystemCompanyStationDetail {
  company: PlatformSystemCompanyStationContext;
  assignment: PlatformSystemStationAssignmentSummary;
  activeDeviceClaim: PlatformSystemStationDeviceClaimSummary | null;
  deviceClaimHistory: PlatformSystemStationDeviceClaimSummary[];
  latestReadinessReport: PlatformSystemStationReadinessReportDetail | null;
  dataAvailability: PlatformSystemStationDataAvailabilityRow[];
  supportDiagnostics: PlatformSystemStationSupportDiagnosticRow[];
}

export interface GetPlatformSystemCompanyStationDetailResponse {
  stationDetail: PlatformSystemCompanyStationDetail;
}

export interface UpdatePlatformSystemCompanyBillingRequest {
  companyId: string;
  billingStatus?: BillingStatus;
  trialExpiresAt?: string | null;
}

export interface UpdatePlatformSystemCompanyBillingResponse {
  companyDetail: PlatformSystemCompanyDetail;
}

export interface UpdatePlatformSystemCompanyPlanBody {
  planCode: PlanCode;
}

export interface UpdatePlatformSystemCompanyPlanRequest
  extends UpdatePlatformSystemCompanyPlanBody {
  companyId: string;
}

export interface UpdatePlatformSystemCompanyPlanResponse {
  companyDetail: PlatformSystemCompanyDetail;
}

export interface UpdatePlatformSystemCompanyStatusBody {
  status: CompanyStatus;
}

export interface UpdatePlatformSystemCompanyStatusRequest
  extends UpdatePlatformSystemCompanyStatusBody {
  companyId: string;
}

export interface UpdatePlatformSystemCompanyStatusResponse {
  companyDetail: PlatformSystemCompanyDetail;
}

export type PlatformSystemFeatureActivationPolicy = 'protected' | 'standard';

export interface PlatformSystemFeatureSummary {
  key: string;
  module: string;
  level: string;
  description: string;
  isActive: boolean;
  activationPolicy: PlatformSystemFeatureActivationPolicy;
  isProtected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformSystemPlanFeatureMapping {
  planCode: PlanCode;
  featureKey: string;
}

export interface PlatformSystemSubscriptionPlanSummary {
  code: PlanCode;
  name: string;
  description: string;
  level: number;
  includedStores: number;
  includedStations: number;
  unlimitedStations: boolean;
  isActive: boolean;
  mappedFeatureKeys: string[];
  activeEnabledFeatureKeys: string[];
  /**
   * Legacy billing catalog alias for mappedFeatureKeys. Runtime company enabled
   * features are active-feature filtered.
   */
  enabledFeatureKeys: string[];
  createdAt: string;
  updatedAt: string;
}

export interface GetPlatformSystemBillingCatalogResponse {
  plans: PlatformSystemSubscriptionPlanSummary[];
  features: PlatformSystemFeatureSummary[];
  planFeatures: PlatformSystemPlanFeatureMapping[];
}

export const platformSystemMaterialEffectiveStateValues = [
  'current_active',
  'current_inactive',
  'future',
  'expired',
] as const;
export type PlatformSystemMaterialEffectiveState =
  (typeof platformSystemMaterialEffectiveStateValues)[number];

export const platformSystemMaterialGovernanceNoteSeverityValues = ['info', 'warning'] as const;
export type PlatformSystemMaterialGovernanceNoteSeverity =
  (typeof platformSystemMaterialGovernanceNoteSeverityValues)[number];

export type PlatformSystemMaterialRateField = 'baseRatePerLb' | 'depositPerPiece' | 'ratePerLb';

export const platformSystemMaterialDoriisOfficialSystemValues = ['DORIIS_CA'] as const;
export type PlatformSystemMaterialDoriisOfficialSystem =
  (typeof platformSystemMaterialDoriisOfficialSystemValues)[number];

export interface PlatformSystemMaterialDoriisMapping {
  officialSystem: PlatformSystemMaterialDoriisOfficialSystem;
  reportingMaterialCode: string;
  reportingMaterialName: string;
  reportingBasis: string;
}

export const platformSystemMaterialRateEffectiveStateValues = [
  'past',
  'current',
  'future',
] as const;
export type PlatformSystemMaterialRateEffectiveState =
  (typeof platformSystemMaterialRateEffectiveStateValues)[number];

export const platformSystemMaterialRateMutationKindValues = [
  'scheduled',
  'updated',
  'unchanged',
] as const;
export type PlatformSystemMaterialRateMutationKind =
  (typeof platformSystemMaterialRateMutationKindValues)[number];

export interface ListPlatformSystemMaterialsRequest {
  effectiveOn?: string;
  includeInactive?: boolean;
  includeFuture?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface GetPlatformSystemMaterialDetailRequest {
  materialId: string;
  effectiveOn?: string;
  includeInactive?: boolean;
  includeFuture?: boolean;
}

export interface PlatformSystemMaterialPresentationFact {
  type: MaterialPresentationType;
  unit: MaterialCaptureUnit;
  captureType: MaterialCaptureType;
  allowsFractional: boolean;
  isCrvEligible: boolean;
  isReportedToDoriis: boolean;
  presentationOrder: number | null;
}

export interface PlatformSystemMaterialRateDiagnostics {
  materialId: string;
  priceVersionId: string;
}

export interface PlatformSystemMaterialRateFact {
  presentationType: MaterialPresentationType;
  rateField: PlatformSystemMaterialRateField;
  rateLabel: string;
  amount: number;
  effectiveState: PlatformSystemMaterialRateEffectiveState;
  effectiveAtUtc: string;
  createdAt: string;
  updatedAt: string;
  diagnostics: PlatformSystemMaterialRateDiagnostics;
}

export interface PlatformSystemMaterialVersionDiagnostics {
  materialId: string;
  materialVersionId: string;
}

export interface PlatformSystemMaterialVersionFact {
  code: string | null;
  displayName: string;
  categoryOrder: number | null;
  materialOrder: number | null;
  isActive: boolean;
  isCrvEligible: boolean;
  doriisMapping: PlatformSystemMaterialDoriisMapping | null;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  presentations: PlatformSystemMaterialPresentationFact[];
  diagnostics: PlatformSystemMaterialVersionDiagnostics;
}

export interface PlatformSystemMaterialDiagnostics {
  materialId: string;
  currentVersionId: string | null;
  sourceTables: string[];
  seedOwned: true;
  mutationSupported: boolean;
  generatedAt: string;
}

export interface PlatformSystemMaterialSummary {
  code: string | null;
  displayName: string;
  categoryOrder: number | null;
  materialOrder: number | null;
  isActive: boolean;
  isCrvEligible: boolean;
  doriisMapping: PlatformSystemMaterialDoriisMapping | null;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  effectiveState: PlatformSystemMaterialEffectiveState;
  effectiveFrom: string;
  effectiveTo: string | null;
  presentations: PlatformSystemMaterialPresentationFact[];
  currentRates: PlatformSystemMaterialRateFact[];
  warnings: string[];
  diagnostics: PlatformSystemMaterialDiagnostics;
}

export interface PlatformSystemMaterialGovernanceNote {
  severity: PlatformSystemMaterialGovernanceNoteSeverity;
  code: string;
  title: string;
  detail: string;
}

export interface PlatformSystemMaterialReportingFact {
  label: string;
  value: string;
}

export interface PlatformSystemMaterialDetail extends PlatformSystemMaterialSummary {
  versionHistory: PlatformSystemMaterialVersionFact[];
  priceHistory: PlatformSystemMaterialRateFact[];
  reportingFacts: PlatformSystemMaterialReportingFact[];
  governanceNotes: PlatformSystemMaterialGovernanceNote[];
}

export interface ListPlatformSystemMaterialsResponse {
  effectiveOn: string;
  includeInactive: boolean;
  includeFuture: boolean;
  items: PlatformSystemMaterialSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface GetPlatformSystemMaterialDetailResponse {
  material: PlatformSystemMaterialDetail;
}

export interface PlatformSystemMaterialRateChangeFields {
  presentationType: MaterialPresentationType;
  effectiveOn: string;
  baseRatePerLb?: number;
  depositPerPiece?: number;
  ratePerLb?: number;
}

export type SchedulePlatformSystemMaterialRateBody = PlatformSystemMaterialRateChangeFields;

export interface SchedulePlatformSystemMaterialRateRequest
  extends SchedulePlatformSystemMaterialRateBody {
  materialId: string;
}

export type UpdatePlatformSystemMaterialRateBody = PlatformSystemMaterialRateChangeFields;

export interface UpdatePlatformSystemMaterialRateRequest
  extends UpdatePlatformSystemMaterialRateBody {
  materialId: string;
  priceVersionId: string;
}

export interface PlatformSystemMaterialRateMutationMetadata {
  kind: PlatformSystemMaterialRateMutationKind;
  materialId: string;
  priceVersionId: string;
  presentationType: MaterialPresentationType;
  effectiveOn: string;
  permissionKey: string;
  auditWritten: boolean;
}

export interface MutatePlatformSystemMaterialRateResponse {
  material: PlatformSystemMaterialDetail;
  priceVersion: PlatformSystemMaterialRateFact;
  mutation: PlatformSystemMaterialRateMutationMetadata;
}

export const platformSystemMaterialActivationStateValues = ['future_active'] as const;
export type PlatformSystemMaterialActivationState =
  (typeof platformSystemMaterialActivationStateValues)[number];

export const platformSystemMaterialActivationReadinessSeverityValues = [
  'blocker',
  'warning',
] as const;
export type PlatformSystemMaterialActivationReadinessSeverity =
  (typeof platformSystemMaterialActivationReadinessSeverityValues)[number];

export const platformSystemMaterialActivationReadinessStatusValues = ['ready', 'blocked'] as const;
export type PlatformSystemMaterialActivationReadinessStatus =
  (typeof platformSystemMaterialActivationReadinessStatusValues)[number];

export interface PlatformSystemMaterialActivationReadinessIssue {
  code: string;
  severity: PlatformSystemMaterialActivationReadinessSeverity;
  message: string;
  field?: string;
  presentationType?: MaterialPresentationType;
}

export interface ActivatePlatformSystemMaterialBody {
  materialVersionId: string;
  effectiveOn: string;
  activationState: PlatformSystemMaterialActivationState;
  confirmFutureActivation: true;
}

export interface ActivatePlatformSystemMaterialRequest extends ActivatePlatformSystemMaterialBody {
  materialId: string;
}

export interface PlatformSystemMaterialActivationResult {
  materialId: string;
  materialVersionId: string;
  activationState: PlatformSystemMaterialActivationState;
  effectiveOn: string;
  readinessStatus: PlatformSystemMaterialActivationReadinessStatus;
  readinessIssues: PlatformSystemMaterialActivationReadinessIssue[];
  activatedAt: string;
  activatedByUserId: string;
}

export interface ActivatePlatformSystemMaterialResponse {
  material: PlatformSystemMaterialDetail;
  activatedVersion: PlatformSystemMaterialVersionFact;
  activation: PlatformSystemMaterialActivationResult;
}

export const platformSystemMaterialDraftReadinessSeverityValues = ['blocker', 'warning'] as const;
export type PlatformSystemMaterialDraftReadinessSeverity =
  (typeof platformSystemMaterialDraftReadinessSeverityValues)[number];

export const platformSystemMaterialDraftReadinessStatusValues = ['ready', 'blocked'] as const;
export type PlatformSystemMaterialDraftReadinessStatus =
  (typeof platformSystemMaterialDraftReadinessStatusValues)[number];

export const platformSystemMaterialDraftStatusValues = ['draft', 'published'] as const;
export type PlatformSystemMaterialDraftStatus =
  (typeof platformSystemMaterialDraftStatusValues)[number];

export const platformSystemMaterialDraftPublicationStateValues = ['inactive'] as const;
export type PlatformSystemMaterialDraftPublicationState =
  (typeof platformSystemMaterialDraftPublicationStateValues)[number];

export interface PlatformSystemMaterialDraftReadinessIssue {
  code: string;
  severity: PlatformSystemMaterialDraftReadinessSeverity;
  message: string;
  field?: string;
  presentationType?: MaterialPresentationType;
}

export interface PlatformSystemMaterialDraftDiagnostics {
  draftId: string;
  reservedFutureMaterialId: string;
  sourceTables: string[];
  createdByUserId: string;
  updatedByUserId: string;
}

export interface PlatformSystemMaterialDraftSummary {
  status: PlatformSystemMaterialDraftStatus;
  code: string;
  normalizedCode: string;
  displayName: string;
  isCrvEligible: boolean;
  doriisMapping: PlatformSystemMaterialDoriisMapping | null;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  presentations: MaterialPresentationType[];
  initialRatePresentationTypes: MaterialPresentationType[];
  readinessStatus: PlatformSystemMaterialDraftReadinessStatus;
  readinessIssues: PlatformSystemMaterialDraftReadinessIssue[];
  publishedAt: string | null;
  publishedByUserId: string | null;
  publishedMaterialId: string | null;
  publishedMaterialVersionId: string | null;
  createdAt: string;
  updatedAt: string;
  diagnostics: PlatformSystemMaterialDraftDiagnostics;
}

export interface PlatformSystemMaterialDraftSwInitialRate {
  presentationType: 'SW';
  effectiveOn: string;
  baseRatePerLb: number;
}

export interface PlatformSystemMaterialDraftScInitialRate {
  presentationType: 'SCL' | 'SCS' | 'SC25';
  effectiveOn: string;
  depositPerPiece: number;
}

export interface PlatformSystemMaterialDraftSpInitialRate {
  presentationType: 'SP';
  effectiveOn: string;
  ratePerLb: number;
}

export type PlatformSystemMaterialDraftInitialRate =
  | PlatformSystemMaterialDraftSwInitialRate
  | PlatformSystemMaterialDraftScInitialRate
  | PlatformSystemMaterialDraftSpInitialRate;

export interface PlatformSystemMaterialDraftDetail extends PlatformSystemMaterialDraftSummary {
  initialRates: PlatformSystemMaterialDraftInitialRate[];
}

export interface ListPlatformSystemMaterialDraftsRequest {
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface ListPlatformSystemMaterialDraftsResponse {
  items: PlatformSystemMaterialDraftSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface GetPlatformSystemMaterialDraftRequest {
  draftId: string;
}

export interface GetPlatformSystemMaterialDraftResponse {
  draft: PlatformSystemMaterialDraftDetail;
}

export interface CreatePlatformSystemMaterialDraftRequest {
  code: string;
  displayName: string;
  isCrvEligible: boolean;
  doriisMapping?: PlatformSystemMaterialDoriisMapping | null;
  maxPiecesPerLine?: number;
  dailyCrvLimitLbs?: number | null;
  presentations?: MaterialPresentationType[];
  initialRates?: PlatformSystemMaterialDraftInitialRate[];
}

export interface CreatePlatformSystemMaterialDraftResponse {
  draft: PlatformSystemMaterialDraftDetail;
}

export interface UpdatePlatformSystemMaterialDraftBody {
  code?: string;
  displayName?: string;
  isCrvEligible?: boolean;
  doriisMapping?: PlatformSystemMaterialDoriisMapping | null;
  maxPiecesPerLine?: number;
  dailyCrvLimitLbs?: number | null;
  presentations?: MaterialPresentationType[];
  initialRates?: PlatformSystemMaterialDraftInitialRate[];
}

export interface UpdatePlatformSystemMaterialDraftRequest
  extends UpdatePlatformSystemMaterialDraftBody {
  draftId: string;
}

export interface UpdatePlatformSystemMaterialDraftResponse {
  draft: PlatformSystemMaterialDraftDetail;
}

export interface PublishPlatformSystemMaterialDraftBody {
  effectiveOn: string;
  publicationState: PlatformSystemMaterialDraftPublicationState;
}

export interface PublishPlatformSystemMaterialDraftRequest
  extends PublishPlatformSystemMaterialDraftBody {
  draftId: string;
}

export interface PlatformSystemMaterialDraftPublicationResult {
  draftId: string;
  publishedMaterialId: string;
  publishedMaterialVersionId: string;
  publicationState: PlatformSystemMaterialDraftPublicationState;
  effectiveOn: string;
  publishedAt: string;
  publishedByUserId: string;
}

export interface PublishPlatformSystemMaterialDraftResponse {
  draft: PlatformSystemMaterialDraftDetail;
  material: PlatformSystemMaterialDetail;
  publication: PlatformSystemMaterialDraftPublicationResult;
}

export interface UpdatePlatformSystemSubscriptionPlanMetadataBody {
  name?: string;
  description?: string;
}

export interface UpdatePlatformSystemSubscriptionPlanMetadataRequest
  extends UpdatePlatformSystemSubscriptionPlanMetadataBody {
  planCode: PlanCode;
}

export interface UpdatePlatformSystemSubscriptionPlanMetadataResponse {
  plan: PlatformSystemSubscriptionPlanSummary;
}

export interface UpdatePlatformSystemSubscriptionPlanActiveStateBody {
  isActive: boolean;
}

export interface UpdatePlatformSystemSubscriptionPlanActiveStateRequest
  extends UpdatePlatformSystemSubscriptionPlanActiveStateBody {
  planCode: PlanCode;
}

export type PlatformSystemSubscriptionPlanActiveStateMutationKind =
  | 'activated'
  | 'deactivated'
  | 'unchanged';

export interface UpdatePlatformSystemSubscriptionPlanActiveStateResponse {
  plan: PlatformSystemSubscriptionPlanSummary;
  mutation: {
    kind: PlatformSystemSubscriptionPlanActiveStateMutationKind;
    assignedCompanyCount: number;
    revokedCompanySessionsCount: number;
    entitlementRevisionBumpCount: number;
  };
}

export interface UpdatePlatformSystemFeatureMetadataBody {
  description?: string;
}

export interface UpdatePlatformSystemFeatureMetadataRequest
  extends UpdatePlatformSystemFeatureMetadataBody {
  featureKey: string;
}

export interface UpdatePlatformSystemFeatureMetadataResponse {
  feature: PlatformSystemFeatureSummary;
}

export interface UpdatePlatformSystemFeatureActiveStateBody {
  isActive: boolean;
}

export interface UpdatePlatformSystemFeatureActiveStateRequest
  extends UpdatePlatformSystemFeatureActiveStateBody {
  featureKey: string;
}

export type PlatformSystemFeatureActiveStateMutationKind =
  | 'activated'
  | 'deactivated'
  | 'unchanged';

export interface UpdatePlatformSystemFeatureActiveStateResponse {
  feature: PlatformSystemFeatureSummary;
  mutation: {
    kind: PlatformSystemFeatureActiveStateMutationKind;
    affectedCompanyCount: number;
    revokedCompanySessionsCount: number;
    entitlementRevisionBumpCount: number;
  };
}

export type PlatformSystemPlanFeatureMappingMutationKind = 'added' | 'removed' | 'unchanged';

export type MutatePlatformSystemPlanFeatureMappingBody = Record<never, never>;

export interface MutatePlatformSystemPlanFeatureMappingRequest {
  planCode: PlanCode;
  featureKey: string;
}

export interface MutatePlatformSystemPlanFeatureMappingResponse {
  plan: PlatformSystemSubscriptionPlanSummary;
  mapping: PlatformSystemPlanFeatureMapping;
  mutation: {
    kind: PlatformSystemPlanFeatureMappingMutationKind;
    affectedCompanyCount: number;
    revokedCompanySessionsCount: number;
  };
}
